import { Badge, Button, Card, Group, Pagination, Select, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconBuildingFactory2, IconPlus, IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EXPORTER_STATUSES, type ExporterDto, type Paginated } from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { AccountStatusBadge } from '@/components/badges';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { ExporterDrawer } from './ExporterDrawer';

const PAGE_SIZE = 25;

export function ExportersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['exporters', 'list', { q, status, page }],
    queryFn: () => http.get<Paginated<ExporterDto>>('/exporters', { q, status, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <PageHeader
        title="Exporters"
        description="Merchant exporter companies and their portal logins."
        actions={
          can('Exporters', 'edit') && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              New exporter
            </Button>
          )
        }
      />
      <Card p={0}>
        <Group p="md" gap="sm">
          <TextInput
            placeholder="Search company, code, contact or country"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            w={{ base: '100%', sm: 340 }}
          />
          <Select
            placeholder="Any status"
            clearable
            data={EXPORTER_STATUSES.map((s) => ({ value: s, label: s[0]!.toUpperCase() + s.slice(1) }))}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            w={160}
          />
        </Group>
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState icon={IconBuildingFactory2} title="No exporters found" />
        ) : (
          <div className="table-scroll">
            <Table miw={720}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Company</Table.Th>
                  <Table.Th>Contact</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>Logins</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((e) => (
                  <Table.Tr key={e.id} className="clickable-row" onClick={() => navigate(`/internal/exporters/${e.id}`)}>
                    <Table.Td>
                      <Text fw={600} fz="sm">
                        {e.companyName}
                      </Text>
                      <Text fz="xs" c="dimmed" ff="monospace">
                        {e.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{e.contactPerson ?? '—'}</Text>
                      {e.email && (
                        <Text fz="xs" c="dimmed">
                          {e.email}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{e.country ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {e.loginCount ? <Badge variant="light">{e.loginCount}</Badge> : <Text fz="sm" c="dimmed">None</Text>}
                    </Table.Td>
                    <Table.Td>
                      <AccountStatusBadge status={e.status} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
        {list.data && (
          <Group justify="space-between" p="md">
            <Text fz="sm" c="dimmed">
              {list.data.total} exporter{list.data.total === 1 ? '' : 's'}
            </Text>
            {pages > 1 && <Pagination total={pages} value={page} onChange={setPage} />}
          </Group>
        )}
      </Card>
      <ExporterDrawer opened={creating} onClose={() => setCreating(false)} onSaved={(e) => navigate(`/internal/exporters/${e.id}`)} />
    </>
  );
}
